import { Component, OnInit, Input } from '@angular/core';
import { StateStructure } from '@ptl/state';
import { ContentStorageService, GlobalShadowStyleDirective, PtlWindowLocationService } from '@ptl/global-components';
import { ApplicationStatusRetrievalService } from 'src/app/shared/services/application-status-retrieval.service';
import { NgRedux } from '@ptl/angular-redux-store';
import { saveApplicationSubmissionInfo } from '../../../shared/reducers/enrollmentApp.reducer';
import { DateService } from '../../../shared/services/date.service';
import { STATUS_MAP } from '../enums/app-status.enum';
import { PRODUCT_TYPES, PRODUCT_TYPE_TO_GROUP_MAP } from '../../plan-selection/enums/product-groups.enum';
import { ENROLLMENT_FLOW } from '../../form-wrappers/plan-enrollment/individual-plan-enrollment-form/configs/interfaces/enrollment-flow.enum';
import { ApplicationRecord } from '../interfaces/application-record.interface';
import { REVIEW_STATUS } from '../enums/review-status.enum';
import { environment } from 'src/environments/environment';
import { STATUS_OPTION_SERVICE_VALUES } from '../sep-application-review-dashboard/sep-application-review-grid/enums/sep-application-review-grid.enum';
import { PrintButtonService } from '../../shared/components/print-button/services/print-button.service';
import { PRINT_TYPE } from '../../shared/components/print-button/enums/print-type.enum';

const SERVICE_APPROVED_STATUS_VALUE = 'MEMBER ENROLLED';
const SERVICE_REJECTED_STATUS_VALUE = 'REJECTED';
const SERVICE_IN_PROGRESS_STATUS_VALUE = 'IN PROGRESS';

const APPROVED_CSS_CLASSNAME = "approved";
const SUBMITTED_CSS_CLASSNAME = "submitted";
const REJECTED_CSS_CLASSNAME = "rejected";
const PENDING_CSS_CLASSNAME = "pending";
const NOT_STARTED_CSS_CLASSNAME = "not-started";
const PURL_CSS_CLASSNAME = "purl";
const SEP_CSS_CLASSNAME = "sep";

@Component({
    selector: 'app-application-card',
    templateUrl: './application-card.component.html',
    styleUrls: ['./application-card.component.scss'],
    standalone: false
})
export class ApplicationCardComponent extends GlobalShadowStyleDirective implements OnInit {
  @Input() retrieveUrl: string;
  @Input() viewUrl: string;
  @Input() plan: ApplicationRecord;
 
  appStatus: string;
  reviewStatus: string;
  typeLabel: string;
  linkDesc: string;
  effectiveDate: string;

  isSubmittedApp: boolean = false;
  isLoading: boolean = false;

  hideAppStatus: boolean = false;
  showSubmitDate: boolean = true;
  showEffectiveDate: boolean = true;
  showApplicationButton: boolean = true;
  showReviewStatusBar: boolean = false;
  showAppTypeIndicator: boolean = false;

  isPurl: boolean = false;
  isSep: boolean = false;
  isDoubleColumnCard: boolean = false;

  isViewApplicationPrint: boolean = false;

  appType: string; 

  constructor(
    private applicationStatusRetrievalService: ApplicationStatusRetrievalService,
    private windowLocationService: PtlWindowLocationService,
    private dateUtility: DateService,
    private ngRedux: NgRedux<StateStructure>,
    private contentStorage: ContentStorageService,
    private printButtonService: PrintButtonService
  ) {
    super();
  }

  ngOnInit() {
    // Setup the application button based on the plan status
    this.getCustomConfigs();
    this.getDoubleColumnCardBool();

    this.isSep = this.plan.enrollmentObjectJson.enrollment.enrollmentFlow === ENROLLMENT_FLOW.SEP_ELECTRONIC;
    this.isPurl = this.plan.isSubmittedProducerURL === "TRUE" ? true : false;
    this.appType = this.setAppType();
    this.appStatus = this.getStatus(this.plan.combinedStatus);
    this.typeLabel = PRODUCT_TYPE_TO_GROUP_MAP[this.plan.productType];
    this.getReviewStatus();
  }

  getDoubleColumnCardBool(){
    if(!this.hideAppStatus || this.typeLabel){
      this.isDoubleColumnCard = true;
    }else{
      this.isDoubleColumnCard = false;
    }
  }

  getCustomConfigs() {
    if (this.plan.combinedStatus === SERVICE_IN_PROGRESS_STATUS_VALUE) {
      this.linkDesc = 'RETRIEVE APPLICATION';

      // Ensure apps that are in progress and retrieved from the past do not have a past effective date
      this.plan.effectiveDate = this.dateUtility.getValidCoverageDate(this.plan.effectiveDate);
    }
    else {

      this.linkDesc = 'VIEW APPLICATION';
      this.isSubmittedApp = true;

      if (environment.initData.brand.ASPID === "MN") {
        if (this.plan.combinedStatus !== STATUS_OPTION_SERVICE_VALUES.SUBMITTED) {
          this.isSubmittedApp = false;
        }
      }

    }

    // DSNP is an external source so we can't view the app or certain dates
    if (this.plan.productType?.toLowerCase() === PRODUCT_TYPES.DSNP.toLowerCase()) {
      this.hideAppStatus = true;
      this.showApplicationButton = false;
      this.showSubmitDate = false;
      this.showEffectiveDate = false;
    }

    // Setup master button boolean
    const dashboardConfigs = this.contentStorage.getContentMap('dashboard') as Map<any, any>;
    if (dashboardConfigs.size > 0) {
      this.isViewApplicationPrint = dashboardConfigs.get('isViewApplicationPrint') === 'true' ? true : false;
      this.showApplicationButton = dashboardConfigs.get('forceHideApplicationButton') === 'true' ? false : true;
      this.showReviewStatusBar = dashboardConfigs.get('showReviewStatusBar') === 'true' ? true : false;
      this.showAppTypeIndicator = dashboardConfigs.get('showAppTypeIndicator') === 'true' ? true : false;
      if(dashboardConfigs.get('hideAppStatus') != null){
        this.hideAppStatus = dashboardConfigs.get('hideAppStatus') === 'true' ? true : false;
      }

    }
  }
  

  async retrieveApp() {
    this.isLoading = true;

    this.ngRedux.dispatch(saveApplicationSubmissionInfo({
      appId: this.plan.applicationTrackingId,
      submittedDate: this.plan.applicationLastModified,
      submittedPlan: null,
      saveElectionAppIn: [],
      submittedApplicantName: null,
      submittedEffectiveDate: null
    }));

    const valuesSaved: boolean = await this.applicationStatusRetrievalService.applyRetrievedDataToState(
      this.plan.applicationTrackingId,
      this.plan.agencyCode
    );

    if (valuesSaved) {
      if (this.isSubmittedApp) {
        if(this.isViewApplicationPrint) {
          this.openPdfPrintPreview();
        } else {
          this.windowLocationService.goToUrl(this.viewUrl);
        }
      }
      else {
        this.windowLocationService.goToUrl(this.retrieveUrl);
      }

      this.isLoading = false;
    }
    else {
      // If the service did not return data we can't do anything
      this.isLoading = false;
      throw 'no application data found';
    }
  }

  getStatusClass() {
    if (this.plan.combinedStatus === SERVICE_REJECTED_STATUS_VALUE) {
      return REJECTED_CSS_CLASSNAME;
    }
    else if (this.plan.combinedStatus === SERVICE_APPROVED_STATUS_VALUE) {
      return APPROVED_CSS_CLASSNAME;
    }
    else {
      return PENDING_CSS_CLASSNAME;
    }
  }

  getStatus(key) {
    const statusMap: string = STATUS_MAP[key];
    if (statusMap) {
      return statusMap;
    }
    return key;
  }

  getHeaderClass(){
    if(this.isPurl){
      return PURL_CSS_CLASSNAME;
    }else if(this.isSep){
      return SEP_CSS_CLASSNAME;
    }else{
      return this.getStatusClass();
    }  
  };

  getReviewStatus(){
    if(this.plan?.downstreamTrackingStatus && this.isSep){
      switch(this.plan.downstreamTrackingStatus){
        case 'NOT_STARTED': 
          this.reviewStatus = REVIEW_STATUS.SEP_NOT_STARTED;
          break;
        case 'PENDING':
          this.reviewStatus = REVIEW_STATUS.SEP_PENDING;
          break;
        case 'APPROVED':
          this.reviewStatus = REVIEW_STATUS.SEP_APPROVED;
          break;
        case 'DENIED':
          this.reviewStatus = REVIEW_STATUS.SEP_DENIED;
          break;
        case 'SUBMITTED':
          this.reviewStatus = REVIEW_STATUS.SEP_SUBMITTED;
          break;
        default:
          this.reviewStatus = REVIEW_STATUS.SEP_NOT_STARTED;
      }
    }else{
      this.reviewStatus = REVIEW_STATUS.NON_SEP;
    }
  }

  getReviewStatusBarClass(){
    if(this.isSep){
      switch(this.reviewStatus){
        case (REVIEW_STATUS.SEP_NOT_STARTED):
          return NOT_STARTED_CSS_CLASSNAME;
        case (REVIEW_STATUS.SEP_PENDING):
          return PENDING_CSS_CLASSNAME;
        case (REVIEW_STATUS.SEP_APPROVED):
          return APPROVED_CSS_CLASSNAME;
        case (REVIEW_STATUS.SEP_SUBMITTED):
          return APPROVED_CSS_CLASSNAME;
        case(REVIEW_STATUS.SEP_DENIED):
          return REJECTED_CSS_CLASSNAME;
        default:
          return NOT_STARTED_CSS_CLASSNAME;
        }
    }
    
    return SUBMITTED_CSS_CLASSNAME;
  }

  setAppType(){
    if(this.plan.enrollmentObjectJson.enrollment.enrollmentFlow === ENROLLMENT_FLOW.SEP_ELECTRONIC){
      return "SEP";
    }else if(this.plan.enrollmentObjectJson.enrollment.enrollmentFlow === ENROLLMENT_FLOW.OEP){
      return "OEP";
    }else{
      if(this.plan.isSubmittedProducerURL === "TRUE"){
        return "PURL";
      }else{
        this.showAppTypeIndicator = false;
        return "";
      }
    }
  }

  openPdfPrintPreview() {
    const appEnrollObj = this.printButtonService.getEnrollmentAppInfo();

    this.printButtonService.triggerPrintPdf(appEnrollObj, PRINT_TYPE.APPLICATION_FORM);


  }
  
}
